/**
 * "Choose your preferred store currency!" balloon shown to new visitors under
 * the currency selector (BOAT-2013).
 *
 * Shown only while the site sells in more than one currency, 2s after the
 * page settles. Dismissed for good (1y cookie, same lifetime as the
 * `currencyCode` choice it advertises) once the visitor opens the selector
 * — hover, focus, click or the mobile sheet all count — or clicks the X.
 */
const COOKIE = 'currencyToastDismissed'
const COOKIE_OPTIONS = { maxAge: 31536000, sameSite: 'lax' } as const
const SHOW_DELAY_MS = 2000

export const useCurrencyToast = () => {
  // BOAT-2022: one shared flag, seeded from the cookie once per app. A
  // writable useCookie per consumer (desktop menu + mobile strip) left stale
  // copies that, on unmount (client navigation), wrote their `null` back and
  // deleted the cookie — so the X'd toast returned on the next full load.
  const dismissed = useState(
    'currencyToastDismissed',
    () => !!useCookie(COOKIE, { readonly: true }).value,
  )
  const currency = useCurrencyStore()

  // Shared across desktop + mobile consumers: one timer, one visibility flag.
  const ready = useState('currencyToastReady', () => false)

  const visible = computed(
    () => ready.value && !dismissed.value && (currency.list?.length ?? 0) > 1,
  )

  const dismiss = () => {
    if (dismissed.value) return
    dismissed.value = true
    // Written once, outside any component scope, so no unmount can undo it.
    useCookie<string>(COOKIE, COOKIE_OPTIONS).value = '1'
  }

  let timer: ReturnType<typeof setTimeout> | undefined
  onMounted(() => {
    if (dismissed.value || ready.value) return
    timer = setTimeout(() => {
      ready.value = true
    }, SHOW_DELAY_MS)
  })
  onBeforeUnmount(() => clearTimeout(timer))

  return { visible, dismiss }
}
